import { useState, useContext, useEffect } from "react";
import apiClient from "../../services/apiClient";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Collapse,
  message,
  Divider,
} from "antd";
import {
  PlusOutlined,
  FormOutlined,
  DeleteOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import RoleModal from "./RoleModal";
import "./AdminDashboard.css";

const { Title } = Typography;
const { Panel } = Collapse;

const PERMISSION_GROUPS = [
  { key: "rating", label: "Rating" },
  { key: "financial", label: "Financial" },
  { key: "issuer", label: "Issuer" },
  { key: "migration", label: "Migration" },
  { key: "news", label: "News" },
  { key: "user", label: "User Management" },
];
const PERMISSIONS = [
  { key: "read", label: "Read" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
];

const AdminRoles = () => {
  const [roles, setRoles] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [customRoleName, setCustomRoleName] = useState("");
  const [customRoleDesc, setCustomRoleDesc] = useState("");
  const [customPermissions, setCustomPermissions] = useState({});
  const [editingRole, setEditingRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [availablePolicies, setAvailablePolicies] = useState(null);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const { isDark } = useContext(ThemeContext);

  const emptyRole = { name: "", description: "", policyIds: [] };

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const res = await apiClient.get(`${baseUrl}/roles`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });
      if (!res.data.success) {
        throw new Error("Failed to fetch roles - success field is false");
      }
      setRoles(res.data.data);
    } catch (e) {
      setRoles([]);
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Fetch policies only if not already fetched
  const fetchPolicies = async () => {
    if (availablePolicies) return;
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const res = await apiClient.get(`${baseUrl}/policies`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });
      if (!res.data.success) {
        throw new Error("Failed to fetch policy - success field is false");
      }
      setAvailablePolicies(res.data.data);
    } catch (e) {
      setAvailablePolicies(null);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // In handleAdd and handleEdit, fetch policies if needed
  const handleAdd = async () => {
    await fetchPolicies();
    setEditingRole(emptyRole); // Use a stable empty object
    setModalVisible(true);
    setCustomRoleName("");
    setCustomRoleDesc("");
    setCustomPermissions({});
    setSelectedPolicyIds([]);
  };

  const handleEdit = async (role) => {
    setRoleLoading(true);
    try {
      await fetchPolicies();
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      // Fetch role details
      const roleRes = await apiClient.get(`${baseUrl}/roles/${role.id}`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });
      
      // Check if response has the expected structure with success field
      if (!roleRes.data.success) {
        throw new Error("Failed to fetch role - success field is false");
      }
      
      const roleData = roleRes.data.data;
      
      // Extract policy IDs from the policies object
      const policyIds = [];
      if (roleData.policies) {
        Object.values(roleData.policies).forEach((groupPolicies) => {
          if (Array.isArray(groupPolicies)) {
            groupPolicies.forEach((policy) => {
              policyIds.push(policy.id);
            });
          }
        });
      }
      setEditingRole({
        id: role.id,
        name: roleData.name,
        description: roleData.description,
        policyIds: policyIds,
      });
      setModalVisible(true);
    } catch (e) {
      setAvailablePolicies(null);
      setEditingRole(null);
      message.error("Failed to fetch role or policies");
    } finally {
      setRoleLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.delete(`${baseUrl}/roles/${id}`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });
      message.success("Role deleted successfully");
      await fetchRoles(); // Refresh the roles list
    } catch (e) {
      message.error("Failed to delete role");
    }
  };

  // Handle permission change in modal
  const handlePermissionChange = (group, checkedValues) => {
    setCustomPermissions((prev) => ({ ...prev, [group]: checkedValues }));
  };

  // Helper to get all policy IDs for a group
  const getGroupPolicyIds = (group) =>
    availablePolicies && availablePolicies[group]
      ? availablePolicies[group].map((policy) => policy.id)
      : [];

  // Handler to merge checked values from all groups
  const handlePolicyGroupChange = (group, checked) => {
    let allChecked = new Set(selectedPolicyIds);
    // Remove all current group's policy IDs
    getGroupPolicyIds(group).forEach((id) => allChecked.delete(id));
    // Add the new checked values for this group
    checked.forEach((id) => allChecked.add(id));
    setSelectedPolicyIds(Array.from(allChecked));
  };

  // Handle create custom role
  const handleCreateOrEditRole = async ({ name, description, policyIds }) => {
    setRoleLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      if (editingRole && editingRole.id) {
        // Edit mode: update role policies only
        await apiClient.put(
          `${baseUrl}/roles/${editingRole.id}/policies`,
          policyIds
        );
        message.success("Role policies updated successfully");
      } else {
        // Add mode: create new role
        await apiClient.post(
          `${baseUrl}/roles`,
          {
            name,
            description,
            policyIds,
          }
        );
        message.success("Role created successfully");
      }
      setModalVisible(false);
      setEditingRole(null);
      await fetchRoles();
    } catch (e) {
      message.error("Failed to save role");
    } finally {
      setRoleLoading(false);
    }
  };

  const columns = [
    { title: "Role Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<FormOutlined />}
            onClick={() => handleEdit(record)}
            type="default"
            title="Edit Role"
            size="small"
            style={{ 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'white',
              borderColor: '#1890ff',
              color: '#1890ff',
              borderRadius: 8
            }}
          />
          <Popconfirm
            title="Delete Role"
            description="Are you sure you want to delete this role?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okType="danger"
          >
            <Button 
              icon={<DeleteOutlined />} 
              type="default"
              size="small"
              style={{ 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'white',
                borderColor: '#ff4d4f',
                color: '#ff4d4f',
                borderRadius: 8
              }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-dashboard-container">
      {/* Hero Section */}
      <div
        className="admin-roles-hero"
        style={{
          background: '#866953',
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          marginBottom: 32,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          position: 'relative',
          overflow: 'hidden',
          minHeight: 180,
        }}
      >
        {/* Overlay for better text readability */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark 
              ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
            borderRadius: 16,
          }}
        />
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 42,
              color: isDark ? "#fff" : "#1a1a1a",
              textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            }}
          >
            Role Management
          </Title>
          <Typography.Text 
            style={{ 
              fontSize: 20, 
              color: isDark ? "#e6e6e6" : "#4a4a4a",
              textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            Create, edit, and manage user roles and their permissions.
          </Typography.Text>
        </div>
      </div>
      <Divider
        style={{
          margin: 0,
          marginBottom: 32,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Role
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          style={{ width: "100%" }}
          className="custom-table"
        />
      </div>
      <RoleModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleCreateOrEditRole}
        initialValues={editingRole || emptyRole}
        loading={roleLoading}
        availablePolicies={availablePolicies}
        mode={editingRole && editingRole.id ? "edit" : "add"}
      />
    </div>
  );
};

export default AdminRoles;
